import { AI_CONFIG } from '../constants';

interface LLMProvider {
  chat: (userMessage: string, systemPrompt: string) => Promise<string>;
}

export const callLLMWithBackoff = async (
  provider: LLMProvider,
  systemPrompt: string,
  userMessage: string
): Promise<string> => {
  return await provider.chat(userMessage, systemPrompt);
};

const normalizeQuotes = (str: string): string =>
  str
    .replace(/[\u201C\u201D\u00AB\u00BB]/g, '"')
    .replace(/[\u2018\u2019]/g, "'");

const extractJsonObject = (text: string): string | null => {
  const normalized = normalizeQuotes(text);

  const markdownMatch = normalized.match(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/i);
  if (markdownMatch) return markdownMatch[1];

  let braceCount = 0;
  let startIndex = -1;

  for (let i = 0; i < normalized.length; i++) {
    if (normalized[i] === '{') {
      if (startIndex === -1) startIndex = i;
      braceCount++;
    } else if (normalized[i] === '}') {
      braceCount--;
      if (startIndex !== -1 && braceCount === 0) {
        return normalized.substring(startIndex, i + 1);
      }
    }
  }
  return null;
};

export const parseAIResponse = (text: string): any | null => {
  if (!text) return null;
  const jsonStr = extractJsonObject(text);
  if (!jsonStr) return null;

  try {
    return JSON.parse(jsonStr);
  } catch (e) {
    try {
      const repaired = jsonStr.replace(/,\s*([}\]])/g, '$1');
      return JSON.parse(repaired);
    } catch {
      console.error('Failed to parse AI JSON:', e, 'Raw:', jsonStr);
      return null;
    }
  }
};

export const parseAIArrayResponse = (text: string): unknown[] => {
  if (!text) return [];
  const normalized = normalizeQuotes(text);
  const markdownMatch = normalized.match(/```(?:json)?\s*(\[[\s\S]*?\])\s*```/i);
  const arrayStr = markdownMatch ? markdownMatch[1] : (normalized.match(/\[[\s\S]*?\]/)?.[0] || null);

  if (!arrayStr) return [];
  try {
    return JSON.parse(arrayStr);
  } catch {
    try {
      const repaired = arrayStr.replace(/,\s*([}\]])/g, '$1');
      return JSON.parse(repaired);
    } catch {
      return [];
    }
  }
};

interface ValidationResult {
  valid: boolean;
  error?: string;
}

export const validateNodeData = (jsonData: any): ValidationResult => {
  if (!jsonData) return { valid: false, error: 'JSON non trovato' };
  if (jsonData.updateMap === false) return { valid: false, error: 'updateMap è false' };

  const required = ['type', 'title', 'summary'] as const;
  for (const field of required) {
    if (!jsonData[field] || String(jsonData[field]).trim().length === 0) {
      return { valid: false, error: `Campo mancante o vuoto: ${field}` };
    }
  }

  if (!['problem', 'solution'].includes(jsonData.type)) {
    return { valid: false, error: `type non valido: ${jsonData.type}` };
  }

  return { valid: true };
};

export const removeJsonFromResponse = (text: string): string => {
  if (!text) return '';
  return text
    .replace(/```(?:json)?\s*\{[\s\S]*?\}\s*```/gi, '')
    .replace(/```(?:json)?\s*\[[\s\S]*?\]\s*```/gi, '')
    .replace(/^\s*\{[\s\S]*?\}\s*$/g, '')
    .trim();
};

export const detectStopCommand = (text: string): boolean => {
  const normalized = text.trim().toLowerCase();
  const stopKeywords = ['basta', 'stop', 'ok così', 'ok cosi', 'procedi', 'fine', 'ok'];
  return stopKeywords.some(keyword => normalized === keyword || normalized.startsWith(keyword + ' '));
};

export const detectProblemIntent = (text: string): boolean => {
  const trimmed = text.trim().toLowerCase();
  const keywords = ['problema:', 'issue:', 'voglio', 'devo', 'come posso', 'help'];
  return keywords.some(keyword => trimmed.startsWith(keyword));
};

export const getContextualMessage = (nodeType: string, hasProblems: boolean, selectedSolution: unknown): string => {
  if (nodeType === 'problem') return 'Ok, problema mappato. Come lo risolveresti?';
  if (selectedSolution) return 'Nuova idea nel grafo. Avanti o cambia rotta.';
  if (hasProblems) return 'Idea nel grafo. Altra idea o nuovo problema?';
  return 'Done.';
};

interface AnthropicClient {
  messages: {
    create: (params: {
      model: string;
      max_tokens: number;
      system: string;
      messages: { role: string; content: string }[];
    }) => Promise<{ content: { text: string }[] }>;
  };
}

export const callAnthropicWithBackoff = async (
  anthropic: AnthropicClient,
  systemPrompt: string,
  userMessage: string
): Promise<string | undefined> => {
  for (let i = 0; i < AI_CONFIG.RETRY_COUNT; i++) {
    try {
      const message = await anthropic.messages.create({
        model: AI_CONFIG.MODEL,
        max_tokens: AI_CONFIG.MAX_TOKENS,
        system: systemPrompt,
        messages: [{ role: 'user', content: userMessage }]
      });
      return message.content[0].text;
    } catch (error) {
      if (i === AI_CONFIG.RETRY_COUNT - 1) throw error;
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, i) * AI_CONFIG.RETRY_DELAY_BASE));
    }
  }
};
